import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaUsers, FaTrash } from 'react-icons/fa';
import './ApprovedAccounts.css';

const ApprovedAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [accountsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal state for followers
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersData, setFollowersData] = useState(null);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [removingFollower, setRemovingFollower] = useState(null);

  // Fetch approved accounts from API
  const fetchApprovedAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('https://server.tradefxonline.com/admin_master_approve.php', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        // Transform API data to match component structure
        const transformedAccounts = (data.data || []).map((account, index) => ({
          id: account.id || index + 1,
          strategyImage: account.strategyimage ? `https://server.tradefxonline.com/${account.strategyimage}` : '/image.png',
          strategyName: account.strategyname || 'N/A',
          strategyAbout: account.strategyabout || 'No description available',
          email: account.email || 'N/A',
          tradeId: account.trade_id || 'N/A',
          accountType: account.account_name || 'Standard Account',
          commission: account.commission || 'N/A',
          leverage: account.leverage || 'N/A',
          currency: account.currency || 'USD',
          minDeposit: account.mindeposit || 'N/A',
          registeredDate: account.Registered_Date || 'N/A',
          server: account.server || 'N/A',
          tradePlatform: account.tradePlatform || 'N/A',
          balance: account.Balance || 0,
          equity: account.equity || 0,
          status: 'Approved'
        }));
        
        setAccounts(transformedAccounts);
      } else {
        throw new Error(data.message || 'Failed to fetch approved accounts');
      }
    } catch (err) {
      console.error('Error fetching approved accounts:', err);
      setError(err.message || 'Failed to load approved accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchApprovedAccounts();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchApprovedAccounts();
  };

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account =>
    account.strategyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.tradeId.includes(searchTerm)
  );

  // Pagination logic
  const indexOfLastAccount = currentPage * accountsPerPage;
  const indexOfFirstAccount = indexOfLastAccount - accountsPerPage;
  const currentAccounts = filteredAccounts.slice(indexOfFirstAccount, indexOfLastAccount);
  const totalPages = Math.ceil(filteredAccounts.length / accountsPerPage);

  const handleViewStats = async (account) => {
    try {
      setSelectedAccount(account);
      setLoadingFollowers(true);
      setShowFollowersModal(true);
      setFollowersData(null);
      
      const response = await fetch('https://server.tradefxonline.com/admin_masters_followers.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tradeid: account.tradeId }),
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setFollowersData(data);
      } else {
        // Set error data to display in modal
        setFollowersData({ error: true, message: data.message || 'No followers found' });
      }
    } catch (err) {
      console.error('Error fetching followers:', err);
      setFollowersData({ error: true, message: 'Failed to load followers' });
    } finally {
      setLoadingFollowers(false);
    }
  };

  const handleRemoveFollower = async (follower) => {
    if (!window.confirm(`Are you sure you want to remove follower "${follower.slave_name}" (${follower.slave_trade})?`)) {
      return;
    }

    try {
      setRemovingFollower(follower.slave_trade);
      
      const response = await fetch('https://server.tradefxonline.com/admin_remove_follower.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mastertrade: selectedAccount.tradeId,
          slavetrade: follower.slave_trade,
          email: selectedAccount.email,
        }),
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success('Follower removed successfully');
        // Refresh followers list
        handleViewStats(selectedAccount);
      } else {
        toast.error(data.message || 'Failed to remove follower');
      }
    } catch (err) {
      console.error('Error removing follower:', err);
      toast.error('Failed to remove follower');
    } finally {
      setRemovingFollower(null);
    }
  };

  const closeFollowersModal = () => {
    setShowFollowersModal(false);
    setFollowersData(null);
    setSelectedAccount(null);
  };

  const handleSettle = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    if (account) {
      console.log('Settling account:', account.strategyName);
      // TODO: Implement settlement logic here
      // This could involve making an API call to settle the account
    }
  };

  return (
    <div className="approved-accounts-container">
      <div className="page-header">
        <h1 className="page-title">Approved Accounts</h1>
        <button 
          onClick={handleRefresh}
          disabled={loading || refreshing}
          className="refresh-btn"
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={handleRefresh} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by strategy name, email, or trade ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          disabled={loading}
        />
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading approved accounts...</p>
        </div>
      ) : (
      <div className="accounts-table-container">
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Strategy Image</th>
              <th>Strategy Name</th>
              <th>Email</th>
              <th>Trade ID</th>
              <th>Account Type</th>
              <th>Server</th>
              <th>Platform</th>
              <th>Balance</th>
              <th>Commission</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentAccounts.map((account) => (
              <tr key={account.id}>
                <td>
                  <div className="strategy-image-container">
                    <img
                      src={account.strategyImage}
                      alt={account.strategyName}
                      className="strategy-image"
                      onError={(e) => {
                        e.target.src = '/image.png';
                      }}
                    />
                  </div>
                </td>
                <td>
                  <div className="strategy-name">
                    <strong>{account.strategyName}</strong>
                    <div className="strategy-about">
                      {account.strategyAbout}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="email-cell">
                    {account.email}
                  </div>
                </td>
                <td>
                  <div className="trade-id">
                    {account.tradeId}
                  </div>
                </td>
                <td>
                  <div className="account-type">
                    {account.accountType}
                  </div>
                </td>
                <td>
                  <div className="server-info">
                    {account.server}
                  </div>
                </td>
                <td>
                  <div className="platform-info">
                    {account.tradePlatform}
                  </div>
                </td>
                <td>
                  <div className="balance-info">
                    ${account.balance.toFixed(2)}
                    <div className="equity-info">
                      Equity: ${account.equity.toFixed(2)}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="commission">
                    {account.commission}
                  </div>
                </td>
                <td>
                  <span className="status-badge approved">
                    {account.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handleViewStats(account)}
                    >
                      <FaUsers style={{ marginRight: '5px' }} />
                      View Stats
                    </button>
                 
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {!loading && filteredAccounts.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstAccount + 1} to {Math.min(indexOfLastAccount, filteredAccounts.length)} of {filteredAccounts.length} accounts
          </div>
          <div className="pagination-controls">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <button
                  key={number}
                  onClick={() => setCurrentPage(number)}
                  className={`page-number ${currentPage === number ? 'active' : ''}`}
                >
                  {number}
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {!loading && filteredAccounts.length === 0 && !error && (
        <div className="no-data-message">
          <p>No approved accounts found.</p>
        </div>
      )}

      {/* Followers Modal */}
      {showFollowersModal && (
        <div className="modal-overlay" onClick={closeFollowersModal}>
          <div className="followers-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FaUsers style={{ marginRight: '10px' }} />
                Followers - {selectedAccount?.strategyName}
              </h2>
              <button className="close-btn" onClick={closeFollowersModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              {loadingFollowers ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading followers...</p>
                </div>
              ) : followersData?.error ? (
                <div className="no-followers">
                  <p>{followersData.message}</p>
                </div>
              ) : followersData ? (
                <>
                  <div className="followers-summary">
                    <div className="summary-card">
                      <div className="summary-label">MASTER TRADE ID:</div>
                      <div className="summary-value">{followersData.master_tradeid}</div>
                    </div>
                    <div className="summary-card">
                      <div className="summary-label">MASTER NAME:</div>
                      <div className="summary-value">{followersData.master_name}</div>
                    </div>
                    <div className="summary-card highlight-card">
                      <div className="summary-label">TOTAL FOLLOWERS:</div>
                      <div className="summary-value highlight">{followersData.followers_count}</div>
                    </div>
                  </div>

                  {followersData.followers && followersData.followers.length > 0 ? (
                    <div className="followers-table-container">
                      <table className="followers-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Trade ID</th>
                            <th>Balance</th>
                            <th>Account</th>
                            <th>Leverage</th>
                            <th>Joined Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {followersData.followers.map((follower) => (
                            <tr key={follower.id}>
                              <td>{follower.id}</td>
                              <td>
                                <div className="follower-name">
                                  {follower.slave_name}
                                  {follower.slave_email && (
                                    <div className="follower-email">{follower.slave_email}</div>
                                  )}
                                </div>
                              </td>
                              <td>{follower.slave_trade}</td>
                              <td>${parseFloat(follower.balance || 0).toFixed(2)}</td>
                              <td>{follower.account}</td>
                              <td>{follower.leverage}</td>
                              <td>{follower.joined_date}</td>
                              <td>
                                <button
                                  className="remove-follower-btn"
                                  onClick={() => handleRemoveFollower(follower)}
                                  disabled={removingFollower === follower.slave_trade}
                                >
                                  {removingFollower === follower.slave_trade ? (
                                    'Removing...'
                                  ) : (
                                    <>
                                      <FaTrash /> Remove
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-followers">
                      <p>No followers found for this master account.</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-followers">
                  <p>Failed to load followers data.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovedAccounts;